import { watch } from 'vue';
import { useMySajuAPI } from '@/composables/calendar/mySaju/useMySajuAPI';

/** '내 사주' 그룹 내 목록 */
const mySajuList = ref(null);

/** 선택된 '내 사주' guestUserId */
const selectedMySajuId = ref(null);

/** 로그아웃 시 사주 목록 초기화 함수 */
export const clearMySajuState = () => {
  mySajuList.value = null;
  selectedMySajuId.value = null;
};

export const useMySaju = ({ userId, errorMessage }) => {
  /** '내 사주' 데이터 */
  const mySajuData = ref({});

  /** [Fix] 로그아웃 시 (userId가 null로 변경) 사주 목록 초기화 */
  watch(
    userId,
    (newUserId) => {
      if (!newUserId) {
        mySajuList.value = null;
        selectedMySajuId.value = null;
        mySajuData.value = {};
      }
    },
    { immediate: false },
  );

  /** '내 사주' 정보 유무 확인 */
  const isMySajuDataExist = computed(() => Object.keys(mySajuData.value).length !== 0);

  /** '내 사주 목록' 데이터 유무 확인 */
  const isMySajuListExist = computed(
    () => mySajuList.value && Object.keys(mySajuList.value).length !== 0,
  );

  /** 내 사주 데이터 관리 */
  const {
    isMySajuLoading,
    isMySajuLoaded,
    getMySajuId,
    getMySajuDetailById,
    getMySajuList,
    setMainMySaju,
  } = useMySajuAPI({ errorMessage });

  /** '내 사주' 정보 조회 */
  const getMySaju = async () => {
    isMySajuLoading.value = true;
    isMySajuLoaded.value = false;

    try {
      const mySajuId = await getMySajuId();

      if (!mySajuId) {
        console.warn('내 사주 ID가 없습니다.');
        mySajuData.value = {};
        return;
      }

      const sajuDetail = await getMySajuDetailById(mySajuId);

      if (sajuDetail) {
        mySajuData.value = sajuDetail;
        isMySajuLoaded.value = true;
      } else {
        console.warn('사주 상세 정보를 가져오지 못했습니다.');
        mySajuData.value = {};
      }
    } catch (e) {
      console.error('내 사주 정보 조회 실패:', e);
    } finally {
      isMySajuLoading.value = false;
    }
  };

  /**
   * 내 사주 목록 가져오기
   * - 대표사주 맨 앞으로 이동
   * - 내 사주 가져오기
   */
  const fetchMySajuList = async (id, yearMonth) => {
    // [Fix] userId가 없으면 (로그아웃 상태) 목록 초기화 후 종료
    if (!userId.value && !id) {
      mySajuList.value = null;
      selectedMySajuId.value = null;
      mySajuData.value = {};
      return;
    }

    if (userId.value || id) {
      // [New] 선택된 사주가 없으면 서버에서 현재 활성 사주 ID를 가져와 동기화
      if (!selectedMySajuId.value) {
        try {
          const currentActiveId = await getMySajuId();
          if (currentActiveId) {
            selectedMySajuId.value = currentActiveId;
          }
        } catch (e) {
          console.error('초기 로드 시 활성 사주 조회 실패:', e);
        }
      }

      const params = {
        yearMonth,
      };

      try {
        const result = await getMySajuList(params);
        result.sort((a, b) => Number(b.guestUserId) - Number(a.guestUserId));
        mySajuList.value = result;
      } catch (e) {
        mySajuList.value = null;
      }

      if (mySajuList.value && mySajuList.value.length > 0) {
        // [Mod] 기존에 선택된 사주가 있으면 그 사주를 유지, 없으면 유효한 사주(시간 정보 있는) 우선 선택
        // 단, selectedMySajuId는 'selectMySaju' 호출 시 갱신됨.

        let targetId = selectedMySajuId.value;
        const currentSelectedInList = targetId
          ? mySajuList.value.find((item) => item.guestUserId === targetId)
          : null;

        if (currentSelectedInList) {
          // 기존 선택 유지
          // 리스트 상태 업데이트
          mySajuList.value.forEach((item) => {
            item.isActive = item.guestUserId === targetId;
          });

          // [Fix] mySajuData(상세정보)가 로컬 ref라 초기화되었을 수 있으므로, 내용이 없으면 상세정보를 다시 조회해야 함
          if (Object.keys(mySajuData.value).length === 0) {
            // [Fix] 초기 로드 시 상세 정보만 가져오도록 skipServerUpdate=true 설정
            await selectMySaju(targetId, true);
          }
        } else {
          // 선택된 게 없거나 리스트에서 사라짐 -> 자동 선택 로직
          const validSaju = mySajuList.value.find(
            (item) => item.birthTime && item.birthTime !== '25:00',
          );

          if (validSaju) {
            targetId = validSaju.guestUserId;
          } else {
            // 유효한 사주도 없으면 선택 안 함
            targetId = null;
          }

          if (targetId) {
            mySajuList.value.forEach((item) => {
              item.isActive = item.guestUserId === targetId;
            });
            // [Fix] 초기 자동 선택 시에는 서버 업데이트 생략 (이미 서버 값이거나, 서버에 값이 없을 때 기본값 설정용이므로)
            await selectMySaju(targetId, true);
          } else {
            mySajuData.value = {};
            mySajuList.value.forEach((item) => {
              item.isActive = false;
            });
          }
        }
      } else {
        await getMySaju();
      }
    } else {
      return;
    }
  };

  /** '내 사주' 목록에서 선택 */
  const selectMySaju = async (id, skipServerUpdate = false) => {
    selectedMySajuId.value = id;

    // [New] 선택한 사주를 대표 사주로 설정 (서버 저장)
    if (id && !skipServerUpdate) {
      try {
        const params = {
          guestUserId: id,
          serviceCategory: 'DAILY_FORTUNE_CALENDAR',
          isDefault: true,
          isActive: true,
        };
        await setMainMySaju(params);
        // refreshMySajuList(); // 필요 시 리스트 갱신 (선택적)
      } catch (e) {
        console.error('대표 사주 설정 실패:', e);
      }
    }

    // [New] 선택한 사주의 상세 정보로 mySajuData 업데이트
    if (id) {
      isMySajuLoading.value = true;
      try {
        const sajuDetail = await getMySajuDetailById(id);
        if (sajuDetail) {
          mySajuData.value = sajuDetail;
          isMySajuLoaded.value = true;
        }
      } catch (e) {
        console.error('사주 상세 조회 실패:', e);
      } finally {
        isMySajuLoading.value = false;
      }
    }

    // [Fix] 리스트 상태 업데이트를 mySajuData 업데이트 이후로 이동
    // (목록에서 '일진 보는중' 뱃지가 잠깐 보이는 현상 방지)
    if (mySajuList.value) {
      mySajuList.value.forEach((item) => {
        item.isActive = item.guestUserId === id;
      });
    }
  };

  // [Fix] userId가 없어도 mySajuList에 데이터가 있으면 반환 (모바일 앱 대응)
  // 로그아웃 시에는 watch에서 mySajuList를 null로 초기화하므로 안전함
  const safeMySajuList = computed(() => {
    return mySajuList.value;
  });

  return {
    mySajuData,
    mySajuList: safeMySajuList,
    isMySajuDataExist,
    isMySajuListExist,
    isMySajuLoading,
    isMySajuLoaded,
    fetchMySajuList,
    selectMySaju,
  };
};
